import type { MatchupAnalysis, MatchupPlayer, MatchupRatings } from '@/types/matchup';

interface Props {
  data: MatchupAnalysis;
}

const DEFAULT_AVATAR = 'https://www.chess.com/bundles/web/images/user-image.007dad08.svg';
const NA = 'n/a';

function getRating(ratings: MatchupRatings, tc: string): number | null {
  return ratings[tc as keyof MatchupRatings]?.current ?? null;
}

function getBestRating(ratings: MatchupRatings, tc: string): number | null {
  return ratings[tc as keyof MatchupRatings]?.best ?? null;
}

function pct(n: number) {
  return `${Math.round(n * 100)}%`;
}

function fmtTrend(n: number): string {
  if (n === 0) return '0';
  return n > 0 ? `+${n}` : `${n}`;
}

// ─── Win-or-lose indicator ─────────────────────────────────────────────────

type Side = 'p1' | 'p2' | 'none';

function winnerOf(a: number | null, b: number | null): Side {
  if (a === null || b === null || a === b) return 'none';
  return a > b ? 'p1' : 'p2';
}

// ─── Duel row: left value ← bar → right value ──────────────────────────────

interface DuelRowProps {
  label: string;
  v1: string | number;
  v2: string | number;
  n1: number | null;   // numeric for bar sizing
  n2: number | null;
  winner?: Side;
}

function DuelRow({ label, v1, v2, n1, n2, winner }: DuelRowProps) {
  // proportional bar: each side fills to share of total
  const total = (n1 ?? 0) + (n2 ?? 0);
  const barW1 = total > 0 && n1 !== null ? Math.round((n1 / total) * 100) : 50;
  const barW2 = 100 - barW1;

  return (
    <div className="mud-row">
      <span className={`mud-row-val mud-row-val--left${winner === 'p1' ? ' mud-row-val--win' : winner === 'p2' ? ' mud-row-val--lose' : ''}`}>
        {v1}
      </span>
      <div className="mud-row-mid">
        <div className="mud-row-label">{label}</div>
        <div className="mud-row-bar" aria-hidden>
          <div className="mud-row-bar-fill mud-row-bar-fill--p1" style={{ width: `${barW1}%` }} />
          <div className="mud-row-bar-fill mud-row-bar-fill--p2" style={{ width: `${barW2}%` }} />
        </div>
      </div>
      <span className={`mud-row-val mud-row-val--right${winner === 'p2' ? ' mud-row-val--win' : winner === 'p1' ? ' mud-row-val--lose' : ''}`}>
        {v2}
      </span>
    </div>
  );
}

// ─── Recent form dots ──────────────────────────────────────────────────────
// Approximate the last N games as W/L/D proportionally.

function FormDots({ wins, losses, draws }: { wins: number; losses: number; draws: number }) {
  const total = wins + losses + draws;
  if (total === 0) return null;
  // Build an approximate 10-slot representation
  const slots = 10;
  const w = Math.round((wins / total) * slots);
  const l = Math.round((losses / total) * slots);
  const d = slots - w - l;
  const items: ('w' | 'l' | 'd')[] = [
    ...Array(Math.max(0, w)).fill('w'),
    ...Array(Math.max(0, d)).fill('d'),
    ...Array(Math.max(0, l)).fill('l'),
  ];
  return (
    <div className="mud-form-dots" aria-label={`${wins}W ${draws}D ${losses}L`}>
      {items.slice(0, 10).map((kind, i) => (
        <span key={i} className={`mud-dot mud-dot--${kind}`} aria-hidden />
      ))}
    </div>
  );
}

// ─── Player column (used in hero) ─────────────────────────────────────────

interface PlayerColProps {
  player: MatchupPlayer;
  probability: number;
  side: 'p1' | 'p2';
  tc: string;
}

function PlayerCol({ player, probability, side, tc }: PlayerColProps) {
  const rating = getRating(player.ratings, tc);
  const trend = player.form.rating_trend;
  const f = player.form;

  return (
    <div className={`mud-player mud-player--${side}`}>
      <div className="mud-player-inner">
        <div className="mud-avatar-wrap">
          <img
            src={player.avatar ?? DEFAULT_AVATAR}
            alt={player.username}
            className="mud-avatar"
            onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_AVATAR; }}
          />
          {player.title && <span className="mud-title-badge">{player.title}</span>}
        </div>

        <div className="mud-player-info">
          <div className="mud-username" title={player.username}>{player.username}</div>

          {rating !== null && (
            <div className="mud-rating-row">
              <span className="mud-rating">{rating}</span>
              <span className={`mud-trend mud-trend--${trend > 0 ? 'up' : trend < 0 ? 'down' : 'flat'}`}>
                {fmtTrend(trend)}
              </span>
            </div>
          )}

          <div className="mud-win-chance">{Math.round(probability * 100)}% win chance</div>

          {f.games_analyzed > 0 && (
            <FormDots wins={f.wins} losses={f.losses} draws={f.draws} />
          )}
        </div>
      </div>

      {/* Strength/weakness tags */}
      {(player.strengths.length > 0 || player.weaknesses.length > 0) && (
        <div className="mud-player-tags">
          {player.strengths.map((s) => (
            <span key={s} className="mud-tag mud-tag--strength">✓ {s}</span>
          ))}
          {player.weaknesses.map((w) => (
            <span key={w} className="mud-tag mud-tag--weakness">✗ {w}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Openings table ────────────────────────────────────────────────────────

function OpeningsTable({ player, tc }: { player: MatchupPlayer; tc: string }) {
  const { top_openings_white: tw, top_openings_black: tb } = player.form;
  if (tw.length === 0 && tb.length === 0) {
    return <p className="mud-no-data">Not enough recent data.</p>;
  }
  return (
    <div className="mud-openings-block">
      <div className="mud-openings-username">{player.username} · {tc}</div>
      {tw.length > 0 && (
        <div className="mud-openings-group">
          <div className="mud-openings-color">
            <span className="mud-piece-sq mud-piece-sq--white" aria-hidden>□</span> As White
          </div>
          {tw.map((o) => (
            <div key={o.opening} className="mud-opening-row">
              <span className="mud-opening-name">{o.opening}</span>
              <span className="mud-opening-count">{o.games}×</span>
            </div>
          ))}
        </div>
      )}
      {tb.length > 0 && (
        <div className="mud-openings-group">
          <div className="mud-openings-color">
            <span className="mud-piece-sq mud-piece-sq--black" aria-hidden>■</span> As Black
          </div>
          {tb.map((o) => (
            <div key={o.opening} className="mud-opening-row">
              <span className="mud-opening-name">{o.opening}</span>
              <span className="mud-opening-count">{o.games}×</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Recommendation card ───────────────────────────────────────────────────

function RecCard({ username, recs, side }: { username: string; recs: string[]; side: 'p1' | 'p2' }) {
  if (recs.length === 0) return null;
  return (
    <div className={`mud-rec-card mud-rec-card--${side}`}>
      <div className="mud-rec-header">
        <span className="mud-rec-label">If you are</span>
        <span className="mud-rec-username">{username}</span>
      </div>
      <ul className="mud-rec-list">
        {recs.map((r) => (
          <li key={r} className="mud-rec-item">
            <span className="mud-rec-bullet" aria-hidden>→</span>
            {r}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────

export default function MatchupView({ data }: Props) {
  const { player1, player2, time_class: tc, win_probability, head_to_head, recommendations } = data;

  const pct1 = Math.round(win_probability.player1 * 100);
  const pct2 = 100 - pct1;

  const r1 = getRating(player1.ratings, tc);
  const r2 = getRating(player2.ratings, tc);
  const best1 = getBestRating(player1.ratings, tc);
  const best2 = getBestRating(player2.ratings, tc);
  const f1 = player1.form;
  const f2 = player2.form;

  const recs1 = recommendations[player1.username] ?? [];
  const recs2 = recommendations[player2.username] ?? [];

  return (
    <section className="mud-results">

      {/* ── Hero: player confrontation ─────────────────────────────── */}
      <div className="mud-hero">
        <PlayerCol player={player1} probability={win_probability.player1} side="p1" tc={tc} />

        <div className="mud-hero-center">
          <span className="mud-tc-pill">{tc.toUpperCase()}</span>
          <div className="mud-vs" aria-hidden>vs</div>
          <div
            className="mud-prob-bar"
            role="meter"
            aria-label={`${player1.username} ${pct1}% vs ${player2.username} ${pct2}%`}
          >
            <div className="mud-prob-fill mud-prob-fill--p1" style={{ width: `${pct1}%` }}>
              {pct1 > 10 && <span className="mud-prob-num">{pct1}%</span>}
            </div>
            <div className="mud-prob-fill mud-prob-fill--p2" style={{ width: `${pct2}%` }}>
              {pct2 > 10 && <span className="mud-prob-num">{pct2}%</span>}
            </div>
          </div>
          <div className="mud-prob-legend">
            <span>Win probability</span>
          </div>
        </div>

        <PlayerCol player={player2} probability={win_probability.player2} side="p2" tc={tc} />
      </div>

      {/* ── Head-to-head ──────────────────────────────────────────── */}
      {head_to_head && head_to_head.total > 0 && (
        <div className="mud-h2h">
          <div className="mud-section-label">Past games</div>
          <div className="mud-h2h-grid">
            <div className="mud-h2h-cell mud-h2h-cell--p1">
              <span className="mud-h2h-num">{head_to_head.player1_wins}</span>
              <span className="mud-h2h-name">{player1.username}</span>
            </div>
            <div className="mud-h2h-cell mud-h2h-cell--draw">
              <span className="mud-h2h-num">{head_to_head.draws}</span>
              <span className="mud-h2h-name">Draws</span>
            </div>
            <div className="mud-h2h-cell mud-h2h-cell--p2">
              <span className="mud-h2h-num">{head_to_head.player2_wins}</span>
              <span className="mud-h2h-name">{player2.username}</span>
            </div>
          </div>
          <div className="mud-h2h-total">{head_to_head.total} games played against each other</div>
        </div>
      )}

      {/* ── Stats duel ────────────────────────────────────────────── */}
      <div className="mud-duel">
        <div className="mud-duel-header">
          <span className="mud-duel-player mud-duel-player--p1">{player1.username}</span>
          <span className="mud-section-label">By the numbers</span>
          <span className="mud-duel-player mud-duel-player--p2">{player2.username}</span>
        </div>

        <div className="mud-duel-rows">
          <DuelRow
            label={`${tc} Rating`}
            v1={r1 ?? NA} v2={r2 ?? NA}
            n1={r1} n2={r2}
            winner={winnerOf(r1, r2)}
          />
          <DuelRow
            label="Peak Rating"
            v1={best1 ?? NA} v2={best2 ?? NA}
            n1={best1} n2={best2}
            winner={winnerOf(best1, best2)}
          />
          {f1.games_analyzed > 0 && f2.games_analyzed > 0 && (
            <DuelRow
              label="Recent Win Rate"
              v1={pct(f1.win_rate)} v2={pct(f2.win_rate)}
              n1={f1.win_rate} n2={f2.win_rate}
              winner={winnerOf(f1.win_rate, f2.win_rate)}
            />
          )}
          {f1.white.total > 0 && f2.white.total > 0 && (
            <DuelRow
              label="Win Rate as White"
              v1={pct(f1.white.win_rate)} v2={pct(f2.white.win_rate)}
              n1={f1.white.win_rate} n2={f2.white.win_rate}
              winner={winnerOf(f1.white.win_rate, f2.white.win_rate)}
            />
          )}
          {f1.black.total > 0 && f2.black.total > 0 && (
            <DuelRow
              label="Win Rate as Black"
              v1={pct(f1.black.win_rate)} v2={pct(f2.black.win_rate)}
              n1={f1.black.win_rate} n2={f2.black.win_rate}
              winner={winnerOf(f1.black.win_rate, f2.black.win_rate)}
            />
          )}
          {(f1.avg_accuracy !== null || f2.avg_accuracy !== null) && (
            <DuelRow
              label="Avg Accuracy"
              v1={f1.avg_accuracy !== null ? `${f1.avg_accuracy}%` : NA}
              v2={f2.avg_accuracy !== null ? `${f2.avg_accuracy}%` : NA}
              n1={f1.avg_accuracy} n2={f2.avg_accuracy}
              winner={winnerOf(f1.avg_accuracy, f2.avg_accuracy)}
            />
          )}
          <DuelRow
            label="Rating Trend (30d)"
            v1={fmtTrend(f1.rating_trend)} v2={fmtTrend(f2.rating_trend)}
            n1={f1.rating_trend + 100} n2={f2.rating_trend + 100}
            winner={winnerOf(f1.rating_trend, f2.rating_trend)}
          />
          {f1.games_analyzed > 0 && f2.games_analyzed > 0 && (
            <DuelRow
              label="Recent Games (W-L-D)"
              v1={`${f1.wins}-${f1.losses}-${f1.draws}`}
              v2={`${f2.wins}-${f2.losses}-${f2.draws}`}
              n1={null} n2={null}
            />
          )}
        </div>
      </div>

      {/* ── Openings ──────────────────────────────────────────────── */}
      <div className="mud-openings">
        <div className="mud-section-label">Favorite openings</div>
        <div className="mud-openings-grid">
          <OpeningsTable player={player1} tc={tc} />
          <OpeningsTable player={player2} tc={tc} />
        </div>
      </div>

      {/* ── Recommendations ───────────────────────────────────────── */}
      {(recs1.length > 0 || recs2.length > 0) && (
        <div className="mud-recs">
          <div className="mud-section-label">Game plan</div>
          <div className="mud-recs-grid">
            <RecCard username={player1.username} recs={recs1} side="p1" />
            <RecCard username={player2.username} recs={recs2} side="p2" />
          </div>
        </div>
      )}

    </section>
  );
}
